'use client';
import { motion } from 'motion/react';

const ritual = [
  { time: '8:00', activity: 'Open DMS export', tool: 'Excel', pain: '15 min to open and filter' },
  { time: '8:15', activity: 'Check WhatsApp updates', tool: 'WhatsApp', pain: 'Anecdotal, no structure' },
  { time: '8:30', activity: 'Call regional managers', tool: 'Phone', pain: '"Yes sir, everything fine"' },
  { time: '9:00', activity: 'Open scheme tracker', tool: 'Excel', pain: "Can't tell if scheme works" },
  { time: '9:30', activity: 'Form an opinion', tool: 'Gut feel', pain: 'No structured reasoning' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function Problem() {
  return (
    <section
      id="product"
      className="py-24 sm:py-32"
      aria-labelledby="problem-heading"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Section label */}
        <motion.p
          className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          The Problem
        </motion.p>
        <motion.h2
          id="problem-heading"
          className="mt-4 max-w-3xl font-serif text-3xl font-700 leading-tight text-foreground sm:text-4xl md:text-5xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.05 }}
          variants={fadeUp}
        >
          Every morning starts with 90 minutes of chaos.
        </motion.h2>

        {/* ── BEFORE → AFTER visual ── */}
        <motion.div
          className="mt-14 sm:mt-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          variants={fadeUp}
        >
          <div className="grid gap-6 md:grid-cols-2 md:gap-0">
            {/* BEFORE */}
            <div className="relative rounded-2xl border border-border bg-white p-8 md:rounded-r-none md:border-r-0 sm:p-10">
              {/* Faint red wash */}
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-r-none"
                aria-hidden="true"
                style={{
                  background:
                    'linear-gradient(135deg, hsl(0 60% 95% / 0.5), transparent 60%)',
                }}
              />
              <div className="relative">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-400">
                  Before
                </p>
                {/* Giant 90 with strike */}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="relative font-serif text-7xl font-700 text-foreground/10 sm:text-8xl md:text-9xl">
                    90
                    {/* Strike-through line */}
                    <span
                      className="absolute top-1/2 -left-2 -right-2 h-1 -rotate-6 rounded-full bg-red-400/60"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="text-lg text-muted-foreground/60 sm:text-xl">
                    min
                  </span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  Excel, WhatsApp, phone calls, gut feel.
                  <br />
                  One or two opinions. Often wrong.
                </p>
              </div>
            </div>

            {/* AFTER */}
            <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-white p-8 md:rounded-l-none sm:p-10">
              {/* Warm amber wash */}
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-l-none"
                aria-hidden="true"
                style={{
                  background:
                    'linear-gradient(135deg, hsl(30 95% 52% / 0.06), transparent 60%)',
                }}
              />
              <div className="relative">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
                  After &mdash; Supe.Market
                </p>
                {/* Giant 10 */}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif text-7xl font-700 text-foreground sm:text-8xl md:text-9xl">
                    10
                  </span>
                  <span className="text-lg text-foreground sm:text-xl">
                    min
                  </span>
                </div>
                <p className="mt-3 text-sm text-foreground">
                  Data-driven decisions. Every crore accounted for.
                  <br />
                  Anomalies named. Actions sent. Outcomes tracked.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── The morning ritual breakdown ── */}
        <motion.div
          className="mt-16 sm:mt-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          <p className="mb-6 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            The morning ritual we&apos;re replacing
          </p>
          <div className="divide-y divide-border">
            {ritual.map((step) => (
              <div
                key={step.time}
                className="flex items-center gap-5 py-4 sm:gap-8"
              >
                <span className="w-12 shrink-0 font-mono text-sm text-muted-foreground/40">
                  {step.time}
                </span>
                <span className="flex-1 text-sm text-foreground sm:text-base">
                  {step.activity}
                </span>
                <span className="hidden text-sm text-muted-foreground sm:block">
                  {step.pain}
                </span>
                <span className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {step.tool}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
